import { authoredLines } from '@lite-workspace/text-layout';
import { resolveVerticalAlign } from '@lite-workspace/shared/render-model';
import type { RenderRect, RenderTextBox } from '@lite-workspace/shared/canvas-engine/core';

import type { TextFrameFit } from './glyph-layout-backend';

/** Layout probe: how far `text` spills past `box`'s frame, measured by the renderer's own pass. */
export type FrameFitProbe = (box: RenderTextBox, text: string) => TextFrameFit;

/** Sub-pixel drift is not a resize — below this the frame is left byte-identical. */
const AUTO_WIDTH_EPSILON_PX = 0.5;

/** Floor for a hugging frame, in em of the box font — a frame collapsed to nothing has no face left to grab. */
const MIN_FRAME_EM = 0.25;

/**
 * Остаточный масштаб глифов, который отрисовка накладывает поверх раскладки. Для бокса он постоянен, поэтому сеанс
 * ввода меряет его ОДИН раз: `widthOver` в point-режиме живёт в до-масштабном пространстве, и без этого числа его
 * не перевести в px рамки. Два замера (по нулевой и по текущей рамке) дают его точно, без догадок о трансформе.
 */
export function residualScaleX(box: RenderTextBox, fit: FrameFitProbe): number | undefined {
  const text = box.text ?? '';
  const point: RenderTextBox = { ...box, textMode: 'free', text };
  const atZero = fit({ ...point, rect: { ...box.rect, width: 0 } }, text);
  const atFrame = fit(point, text);
  const framePerScale = atZero.widthOver - atFrame.widthOver;
  if (!(framePerScale > 0) || !(box.rect.width > 0)) return undefined;
  return box.rect.width / framePerScale;
}

/**
 * Рамка для копии текстового слоя — или `undefined`, если копию надо оставить ровно такой, как оригинал.
 *
 * Авто-ширину получает только текст, который лёг В ОДНУ строку: его продолжают набирать строкой, а рамка идёт
 * следом, пока фотограф не поправит ширину руками. Многострочный блок (цитата, свёрстанное четверостишие) копируется
 * как есть: его строки — это и есть замысел вёрстки, а измерение «естественной ширины» вытянуло бы весь абзац
 * в одну длинную строку. Не удалось померить (шрифт не разрешился) — тоже копируем как есть.
 */
export function textCloneFrame(box: RenderTextBox, fit: FrameFitProbe): RenderRect | undefined {
  const text = box.text ?? '';
  if (fit(box, text).lines !== 1) return undefined;
  return autoWidthRect(box, text, fit) ?? box.rect;
}

/**
 * Рамка блочного текста с авто-шириной: обжимает содержимое СО ВСЕХ СТОРОН, пока пользователь не заберёт ширину
 * себе (ручной ресайз снимает авто-ширину — applyBoxRects).
 *
 * Такой блок никогда не переносит строку по рамке — рамка всегда ровно по содержимому, — поэтому визуально он
 * ведёт себя как фигмовская «Автоширина», но остаётся блочным, и его rect накрывает нарисованные глифы
 * (hit-test движка бьёт ровно по rect, см. `hit_test.rs`). Enter растит рамку вниз, удаление строк поджимает.
 *
 * `scaleX` передаёт сеанс ввода: с ним хватает ОДНОГО замера на нажатие. Без него замеров три, а на длинном тексте
 * один проход раскладки стоит ~15 мс — набор начинает заметно отставать от клавиатуры.
 */
export function autoWidthRect(
  box: RenderTextBox,
  text: string,
  fit: FrameFitProbe,
  scaleX?: number,
  spaceAdvance?: number,
  measureBare?: (bare: string) => number,
): RenderRect | undefined {
  if (!text) return undefined; // пустой буфер — рамку не схлопываем, пусть держит вход в редактирование
  const natural = naturalTextSize(box, text, fit, scaleX, spaceAdvance, measureBare);
  if (!natural) return undefined;
  const width = Math.max(natural.width, box.fontSize * MIN_FRAME_EM);
  // Height hugs the INK, not the leading: on a PSD layer whose leading dwarfs its glyphs (396px of line height over
  // 251px of ink) a leading floor would leave a visible band of empty frame under a single line.
  const height = Math.max(natural.height, 1);
  if (Math.abs(width - box.rect.width) < AUTO_WIDTH_EPSILON_PX && Math.abs(height - box.rect.height) < AUTO_WIDTH_EPSILON_PX)
    return undefined;
  return anchoredRect(box, width, height);
}

/**
 * Фиксированный блок («Фиксированный размер»): ширину держит пользователь, текст переносится по ней, а ВЫСОТА
 * подстраивается под набранное — Enter и перенос растят рамку вниз, удаление строк поджимает её обратно.
 */
export function fixedHeightRect(box: RenderTextBox, text: string, fit: FrameFitProbe): RenderRect | undefined {
  if (!text) return undefined;
  const probe = fit({ ...box, textMode: 'block', text }, text);
  const height = Math.max(box.rect.height + probe.heightOver, box.lineHeight || box.fontSize);
  if (Math.abs(height - box.rect.height) < AUTO_WIDTH_EPSILON_PX) return undefined;
  return anchoredRect(box, box.rect.width, height);
}

/**
 * Естественный размер текста в canvas-px рамки.
 *
 * Меряем point-режимом по НУЛЕВОЙ рамке: `widthOver` там равен ширине содержимого, а `heightOver` — ink-высоте минус
 * высота рамки. Отдельный блочный замер высоты не нужен: авто-ширина никогда не переносит строку, поэтому её
 * раскладка совпадает с point-раскладкой того же текста.
 */
function naturalTextSize(
  box: RenderTextBox,
  text: string,
  fit: FrameFitProbe,
  scaleX?: number,
  spaceAdvance?: number,
  measureBare?: (bare: string) => number,
): { width: number; height: number } | undefined {
  const k = scaleX ?? residualScaleX({ ...box, text }, fit);
  if (k === undefined || !(k > 0)) return undefined;
  const measure = (t: string): number =>
    Math.max(0, fit({ ...box, textMode: 'free', text: t, rect: { ...box.rect, width: 0 } }, t).widthOver);
  const probe = fit({ ...box, textMode: 'free', text, rect: { ...box.rect, width: 0 } }, text);
  return {
    width: heldWidth(authoredLines(text, box.textLayout), Math.max(0, probe.widthOver), measureBare ?? measure, spaceAdvance) * k,
    height: box.rect.height + probe.heightOver,
  };
}

/**
 * Advance пробела этого бокса в единицах point-замера. Константа шрифта/трекинга: сеанс ввода меряет её ОДИН раз
 * (двумя раскладками по паре символов), а не на каждое нажатие — и мимо мемо-кэша каретки.
 */
export function spaceAdvanceOf(box: RenderTextBox, fit: FrameFitProbe): number {
  const measure = (t: string): number =>
    Math.max(0, fit({ ...box, textMode: 'free', text: t, rect: { ...box.rect, width: 0 } }, t).widthOver);

  return Math.max(0, measure(' x') - measure('x'));
}

/**
 * Ширина с учётом пробелов, которые пользователь ДЕРЖИТ в конце строки.
 *
 * Раскладка отбрасывает хвостовой пробел (он не рисуется), поэтому рамка, идущая за замером, застывала, пока
 * набирают пробел, — а вместе с ней и каретка: пробел «появлялся» только со следующей буквой (FKS-11452). Ширину
 * пробела берём у самой раскладки — ведущий пробел она считает. Однострочный текст обходится вовсе без
 * дополнительных замеров: его подрезанная ширина уже измерена основным пробегом.
 */
function heldWidth(lines: readonly string[], measured: number, measure: (t: string) => number, spaceAdvance?: number): number {
  const lastLine = lines[lines.length - 1];
  const held = /[ \t]+$/.exec(lastLine);
  if (!held) return measured;
  const bare = lastLine.slice(0, lastLine.length - held[0].length);
  const advance = spaceAdvance ?? Math.max(0, measure(' ' + bare) - measure(bare));
  // Однострочный: подрезанный замер равен ширине bare-строки — пробелы добавляются без единой лишней раскладки.
  if (lines.length === 1) return measured + advance * held[0].length;

  return Math.max(measured, measure(bare) + advance * held[0].length);
}

/**
 * Рамка растёт от того края, к которому прижат текст: выключка по центру раздаёт прирост поровну, вправо — влево.
 *
 * Считаем через центр, а не через край: повёрнутый бокс движок крутит вокруг центра рамки, поэтому у наивного
 * «подвинуть левый край» центр уезжает и глифы поворачиваются в другое место. Смещение центра берём в повёрнутых
 * осях — тогда точка привязки остаётся на экране там же при любом угле.
 */
function anchoredRect(box: RenderTextBox, width: number, height: number): RenderRect {
  const { x, y, width: w, height: h, rotation } = box.rect;
  const fx = box.align === 'right' ? 1 : box.align === 'center' ? 0.5 : 0;
  const vAlign = resolveVerticalAlign(box.verticalAlign);
  const fy = vAlign === 'bottom' ? 1 : vAlign === 'center' ? 0.5 : 0;
  const localDx = (fx - 0.5) * (w - width);
  const localDy = (fy - 0.5) * (h - height);
  const theta = ((rotation ?? 0) * Math.PI) / 180;
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  const cx = x + w / 2 + localDx * cos - localDy * sin;
  const cy = y + h / 2 + localDx * sin + localDy * cos;
  return { ...box.rect, x: cx - width / 2, y: cy - height / 2, width, height };
}
